/* "Complete your routine" (snippets/oracura-kit.liquid). Add-on cards in a row; Add (or a tap
   on the photograph, or picking a colour) puts one in. The more go in, the bigger the saving on
   the whole set: one add-on 4%, two 8%, three or more 12% (data-tiers), shown by a bar under
   those three steps and one line saying what the next one gets.

   oracura-product.js asks for cartItems() when Add posts, so the add-ons go in the same request
   as the product, and applyDiscount() first puts the matching Shopify code on the cart. The
   codes take their % off these products across the whole cart, so the step is counted on the
   bag as it will be: what is already in it (of these products) and what is going in.
   Money is shown in rupees only. */

const motionOK = () => document.documentElement.dataset.oracuraMotion !== 'paused'
  && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class OracuraKit extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.heroPrice = Number(this.dataset.heroPrice) || 0;
    this.tiers = (this.dataset.tiers || '4,8,12').split(',').map(Number);
    // The page carries the codes scrambled (snippets/oracura-kit.liquid): base64 of the code reversed.
    this.codes = (this.dataset.codes || '').split(',').map(c => { try { return atob(c.trim()).split('').reverse().join(''); } catch (error) { return ''; } });
    this.savings = this.dataset.savings === 'true' && this.dataset.proGift !== 'true';
    const sample = this.dataset.money || '₹1,000';
    this.moneyTemplate = sample.includes('1,000') ? sample.replace('1,000', '{n}') : '₹{n}';
    this.addEventListener('click', e => {
      if (e.target.closest('.oracura-kit__dot')) return;
      const nav = e.target.closest('[data-kit-prev], [data-kit-next]');
      if (nav) { this.slide(nav.hasAttribute('data-kit-next') ? 1 : -1); return; }
      const hit = e.target.closest('[data-kit-add], [data-kit-toggle]');
      if (hit) this.toggle(hit.closest('[data-kit-row]'));
    });
    // The row of cards: arrows and fades where there is more, a scroll bar under it.
    this.rail = this.querySelector('[data-kit-rail]');
    this.cardsEl = this.querySelector('[data-kit-cards]');
    if (this.cardsEl) {
      this.cardsEl.addEventListener('scroll', () => this.railState(), { passive: true });
      window.addEventListener('resize', () => this.railState(), { passive: true });
      requestAnimationFrame(() => this.railState());
    }
    // The cards come in one after the other the first time the box is seen.
    if (motionOK() && 'IntersectionObserver' in window) {
      this.classList.add('is-waiting');
      const seen = new IntersectionObserver(entries => {
        if (!entries.some(en => en.isIntersecting)) return;
        seen.disconnect();
        this.classList.remove('is-waiting');
        this.classList.add('is-in');
        setTimeout(() => this.classList.remove('is-in'), 1400);
      }, { threshold: 0.2 });
      seen.observe(this);
      // Never left hidden: shown as they are if the box is not seen soon.
      setTimeout(() => { if (this.classList.contains('is-waiting')) { seen.disconnect(); this.classList.remove('is-waiting'); } }, 8000);
    }
    this.restoreFromUrl();
    this.addEventListener('change', e => {
      if (!e.target.matches('[data-kit-variant]')) return;
      const row = e.target.closest('[data-kit-row]');
      this.show(row);
      // Picking a colour means wanting it.
      if (!this.isAdded(row)) this.set(row, true);
      else this.refresh(true);
    });
    this.refresh(false);
  }

  /* The page's link carries the box: ?kit=<variant>-<variant>…, one variant id per add-on in
     the colour picked. It is rewritten on every change, so a link copied or shared at any moment
     opens with the same add-ons and colours selected. Only on the product page itself (the one
     that keeps ?variant= in its link too). */
  urlOwner() { return this.closest('oracura-product')?.dataset.updateUrl === 'true'; }

  restoreFromUrl() {
    if (!this.urlOwner()) return;
    const ids = (new URL(location.href).searchParams.get('kit') || '').split(/[-,.\s]+/).filter(id => /^\d+$/.test(id));
    for (const id of ids) {
      const input = this.querySelector(`[data-kit-variant][value="${id}"]`);
      const row = input?.closest('[data-kit-row]');
      if (!row || row.hidden || input.disabled) continue;
      if (input.type === 'radio') input.checked = true;
      this.show(row);
      row.classList.add('is-added');
      row.querySelector('[data-kit-add]')?.setAttribute('aria-pressed', 'true');
    }
  }

  writeUrl() {
    if (!this.urlOwner()) return;
    const url = new URL(location.href);
    const ids = this.added().map(row => this.picked(row)?.value).filter(Boolean);
    if (ids.length) url.searchParams.set('kit', ids.join('-'));
    else url.searchParams.delete('kit');
    if (url.href !== location.href) history.replaceState(history.state, '', url);
  }

  rows() { return [...this.querySelectorAll('[data-kit-row]')].filter(row => !row.hidden); }
  isAdded(row) { return row.classList.contains('is-added'); }
  added() { return this.rows().filter(row => this.isAdded(row)); }
  get count() { return this.added().length; }
  picked(row) { return row.querySelector('[data-kit-variant]:checked') || row.querySelector('[data-kit-variant]'); }

  money(cents) { return this.moneyTemplate.replace('{n}', Math.floor(cents / 100).toLocaleString('en-US')); }

  toggle(row) { if (row) this.set(row, !this.isAdded(row)); }

  // Two cards on, or back.
  slide(dir) {
    const card = this.cardsEl?.querySelector('[data-kit-row]:not([hidden])');
    const step = card ? (card.getBoundingClientRect().width + 12) * 2 : 240;
    this.cardsEl?.scrollBy({ left: dir * step, behavior: motionOK() ? 'smooth' : 'auto' });
  }

  railState() {
    const el = this.cardsEl;
    if (!el || !this.rail) return;
    const max = el.scrollWidth - el.clientWidth;
    this.rail.classList.toggle('has-less', el.scrollLeft > 4);
    this.rail.classList.toggle('has-more', el.scrollLeft < max - 4);
    const track = this.querySelector('[data-kit-thumb-bar]');
    if (track) {
      track.parentElement.hidden = max <= 4;
      const share = el.clientWidth / el.scrollWidth;
      track.style.width = `${Math.max(share * 100, 12)}%`;
      track.style.left = `${max > 0 ? (el.scrollLeft / max) * (100 - Math.max(share * 100, 12)) : 0}%`;
    }
  }

  // A money figure counting from what it said to what it says now.
  countTo(el, cents) {
    if (!el) return;
    const from = Number(el.dataset.cents);
    el.dataset.cents = String(cents);
    if (!motionOK() || !Number.isFinite(from) || from === cents) { el.textContent = this.money(cents); return; }
    cancelAnimationFrame(el.countFrame);
    const start = performance.now();
    const tick = now => {
      const t = Math.min(1, (now - start) / 420);
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = this.money(from + (cents - from) * eased);
      if (t < 1) el.countFrame = requestAnimationFrame(tick);
    };
    el.countFrame = requestAnimationFrame(tick);
  }

  set(row, on) {
    row.classList.toggle('is-added', on);
    const button = row.querySelector('[data-kit-add]');
    if (button) button.setAttribute('aria-pressed', String(on));
    if (on) this.play(row, 'is-popping');
    this.refresh(true);
  }

  // A colour's photograph and price on its card.
  show(row) {
    const v = this.picked(row);
    if (!v) return;
    const img = row.querySelector('[data-kit-thumb]');
    if (img && v.dataset.img) img.src = v.dataset.img;
    const price = row.querySelector('[data-row-price]');
    if (price) price.textContent = this.money(Number(v.dataset.price) || 0);
    // Its MRP, struck through, when the colour has one above the price.
    const mrp = row.querySelector('[data-row-mrp]');
    if (mrp) {
      const m = Number(v.dataset.mrp) || 0, p = Number(v.dataset.price) || 0;
      mrp.hidden = !(m > p);
      mrp.textContent = m > p ? this.money(m) : '';
    }
  }

  play(el, cls, ms = 700) {
    if (!el) return;
    el.classList.remove(cls);
    void el.offsetWidth;
    el.classList.add(cls);
    clearTimeout(el[`t_${cls}`]);
    el[`t_${cls}`] = setTimeout(() => el.classList.remove(cls), ms);
  }

  // The step reached with n add-ons: 0 (none), 1, 2 or 3 (three or more).
  step(n) { return Math.min(Math.max(n, 0), this.tiers.length); }
  pct(n) { const s = this.step(n); return s ? this.tiers[s - 1] : 0; }

  refresh(announce) {
    const n = this.count;
    const sum = this.heroPrice + this.added().reduce((s, row) => s + (Number(this.picked(row)?.dataset.price) || 0), 0);
    const pct = this.savings ? this.pct(n) : 0;
    const save = Math.floor((sum * pct) / 100 / 100) * 100;
    this.total = sum - save;
    this.totalText = this.money(this.total);
    // The add-ons' MRP (their price where they have none), for the page's price row to show the
    // set against (assets/oracura-product.js, kitPrices).
    this.addonsMrp = this.added().reduce((s, row) => { const v = this.picked(row); return s + (Number(v?.dataset.mrp) || Number(v?.dataset.price) || 0); }, 0);

    // The bar and its three marks; a step up shines along the bar and ticks its mark.
    const s = this.step(n);
    const before = this.lastStep ?? s;
    this.lastStep = s;
    const fill = this.querySelector('[data-kit-fill]');
    if (fill) {
      fill.style.width = `${(s / this.tiers.length) * 100}%`;
      if (announce && s > before) this.play(fill, 'is-growing', 1000);
    }
    this.querySelectorAll('[data-kit-mark]').forEach(m => {
      const at = Number(m.dataset.kitMark);
      m.classList.toggle('is-on', at <= s);
      if (announce && at === s && s > before) this.play(m, 'is-reached');
    });
    if (announce && s === this.tiers.length && s > before) this.play(this.querySelector('.oracura-kit__upto'), 'is-best', 1300);

    // One line: what is on, and what the next one gets.
    const offer = this.querySelector('[data-kit-offer]');
    if (offer) {
      const t = this.tiers;
      let html;
      // "Extra": the codes take their % off the prices already marked down, on top of the sale.
      if (s === 0) html = `Add 1 item, get an <b>additional ${t[0]}% off</b>`;
      else if (s < t.length) html = `<b>Additional ${t[s - 1]}% off</b> unlocked · add 1 more for <b>${t[s]}%</b>`;
      else html = `<b>Additional ${t[t.length - 1]}% off</b> unlocked · your best price`;
      if (offer.innerHTML !== html) { offer.innerHTML = html; if (announce) this.play(offer, 'is-new'); }
    }

    // The total, once something is added.
    const box = this.querySelector('[data-kit-sum]');
    if (box) {
      const opening = box.hidden && n > 0;
      box.hidden = n === 0;
      if (announce && opening) this.play(box, 'is-new');
      this.querySelector('[data-kit-items]').textContent = `${n + 1} items`;
      this.countTo(this.querySelector('[data-kit-total]'), this.total);
      const was = this.querySelector('[data-kit-was]');
      was.hidden = !save; if (save) was.textContent = this.money(sum);
      const saveEl = this.querySelector('[data-kit-save]');
      const saveChanged = saveEl.dataset.cents !== String(save);
      saveEl.dataset.cents = String(save);
      saveEl.hidden = !save;
      if (save) saveEl.textContent = `You save ${this.money(save)}`;
      if (announce && save && saveChanged) this.play(saveEl, 'is-new');
    }
    this.writeUrl();
    this.dispatchEvent(new CustomEvent('oracura:kit-change', { bubbles: true }));
  }

  // The page's variant changed: its price, and the add-ons kept for the Lite only.
  heroChanged(v) {
    if (!v) return;
    const gift = this.closest('oracura-product')?.flossGiftIds?.has(Number(v.id)) || false;
    this.dataset.proGift = String(gift);
    this.savings = this.dataset.savings === 'true' && !gift;
    this.querySelectorAll('[data-kit-discount]').forEach(el => { el.hidden = gift; });
    this.heroPrice = Number(v.price) || this.heroPrice;
    const pro = /\bpro\b/i.test(String(v.title || ''));
    this.querySelectorAll('[data-kit-row][data-lite-only]').forEach(row => {
      row.hidden = pro;
      if (pro && this.isAdded(row)) { row.classList.remove('is-added'); row.querySelector('[data-kit-add]')?.setAttribute('aria-pressed', 'false'); }
    });
    this.refresh(false);
  }

  cartItems() { return this.added().map(row => ({ id: Number(this.picked(row).value), quantity: 1 })); }

  addLabel(n) { return (this.dataset.addLabel || 'Add {n} to Bag').replace('{n}', n); }

  // The code for the step the bag will be on: these products already in it, this one, and the
  // add-ons. A code of the shopper's own that the cart takes stays; so does a bigger one of ours.
  async applyDiscount() {
    if (!this.savings || !this.count) return;
    const root = window.Shopify?.routes?.root || '/';
    const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then(r => r.json());
    // Counted as the cart keeper counts (snippets/oracura-kit-codes.liquid): a main product and
    // its own add-ons, on the bag as it will be. Without the keeper, every listed product.
    const hero = this.dataset.heroHandle;
    const bag = (cart.items || []).map(item => ({ handle: item.handle, quantity: item.quantity }))
      .concat([{ handle: hero, quantity: 1 }], this.added().map(row => ({ handle: row.hasAttribute('data-pack') ? hero : row.dataset.handle, quantity: 1 })));
    const eligible = this.dataset.eligible || '|';
    const units = window.OracuraKitCodes?.count
      ? window.OracuraKitCodes.count(bag)
      : bag.reduce((n, item) => n + (eligible.includes(`|${item.handle}|`) ? Number(item.quantity) || 0 : 0), 0);
    const s = this.step(units - 1);
    const code = s && this.codes[s - 1];
    if (!code) return;
    const ours = this.codes.filter(Boolean).map(c => c.toUpperCase());
    // A coupon of the shopper's own that the cart takes stays. One of ours already there, typed
    // or from a link, is put right: the box's own code, marked, which the keeper then follows.
    for (const d of cart.discount_codes || []) {
      if (!ours.includes(String(d.code).toUpperCase()) && d.applicable) return;
    }
    await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      // Marked as the box's (a hidden attribute, with the time), so the cart keeper
      // (snippets/oracura-kit-codes.liquid) looks after this code and no typed-in one.
      body: JSON.stringify({ discount: code, attributes: { _oracura_kit: String(Date.now()) } }),
    });
  }

  // After the items are in the bag the box starts over on the product alone.
  reset() {
    this.rows().forEach(row => { row.classList.remove('is-added'); row.querySelector('[data-kit-add]')?.setAttribute('aria-pressed', 'false'); });
    this.refresh(false);
  }
}

if (!customElements.get('oracura-kit')) customElements.define('oracura-kit', OracuraKit);
